// THE ROLE VOCABULARY — the pure half of rbac.js, split out so code that only
// needs to ask "what role is this?" can do so without pulling in React.
//
// WHY THE SPLIT: rbac.js is a provider — it imports react and ../auth.jsx to
// hang the current user's role off a context. lib/agentRun.js needs exactly one
// thing from it, normalizeRole, to find the roster's REP (whose HubSpot tasks
// the morning briefing shows). Importing rbac.js there dragged auth.jsx into
// every Node test that loads agentRun.js and they died on the .jsx extension.
// Duplicating the table instead would have been worse: two copies of "what
// counts as a rep" drift, and the drift is silent. One table, no React.
//
// rbac.js re-exports all of this, so every existing `from './rbac.js'` import
// keeps working and there is still only one definition.

// Five workspace roles modeled on standard outreach platforms
// (Outreach/Apollo/Salesloft): Owner runs the workspace, Admin runs everything
// but the owner, Manager runs the team's selling motion and sees everyone's
// numbers, Sales Rep works their own book, Viewer looks but never touches.
export const ROLE_DEFS = {
  owner: {
    label: 'Owner',
    blurb: 'Runs the workspace. Full control, including roles and integrations.',
    rank: 5,
  },
  admin: {
    label: 'Admin',
    blurb: 'Everything except changing the Owner. Manages members, settings, and data.',
    rank: 4,
  },
  manager: {
    label: 'Manager',
    blurb: 'Runs the selling motion: the daily operations, exports, and every rep’s numbers.',
    rank: 3,
  },
  rep: {
    label: 'Sales Rep',
    blurb: 'Works leads and their own outreach. Sees their own performance.',
    rank: 2,
  },
  viewer: {
    label: 'Viewer',
    blurb: 'Read-only. Browses leads and dashboards — no sends or edits.',
    rank: 1,
  },
}

export const ROLE_KEYS = Object.keys(ROLE_DEFS)

// Legacy role labels stored by older builds / the invite flow.
const LEGACY = {
  administrator: 'admin',
  admin: 'admin',
  owner: 'owner',
  manager: 'manager',
  member: 'rep',
  'sales rep': 'rep',
  rep: 'rep',
  viewer: 'viewer',
  'read-only': 'viewer',
}

// FOR PERMISSIONS. An unrecognised or missing role falls back to 'rep', the
// least-privileged working role — the safe direction when the question is "how
// much is this person allowed to do?": guess low.
export function normalizeRole(value) {
  return LEGACY[String(value || '').trim().toLowerCase()] || 'rep'
}

// FOR IDENTITY. Returns '' when the role is not one we actually recognise.
//
// WHY THIS EXISTS SEPARATELY: normalizeRole's fallback is the WRONG direction
// for the question "whose HubSpot tasks are these?". There, guessing 'rep'
// OVER-includes: a roster row with a role the LEGACY table has never heard of
// ('SDR', 'Team Lead', 'Account Executive') or with no role field at all — and
// team.js#readLocal will hand back whatever shape localStorage holds — would be
// silently treated as a sales rep, and that person's tasks would appear in the
// briefing under the rep's name. Asking a question about IDENTITY must never
// answer from a fallback designed for a question about PERMISSIONS.
export function roleOrEmpty(value) {
  return LEGACY[String(value || '').trim().toLowerCase()] || ''
}
